<!-- 
Panel for showing a summary of a provider.
-->
<template>
  <div>
    <our-busy-indicator :show="loading"/>
    <our-data-container>
      <our-data-field label="Levy collections" empty="None">
        <div v-for="lcp in levyCollectionProviders" :key="lcp.id">
          <router-link :to="`/levy-collections/${lcp.levyCollectionId}/providers/${lcp.providerId}`">
            {{ lcp.levyCollectionDescription }}
          </router-link>
        </div>
      </our-data-field>
    </our-data-container>
    <v-divider class="mb-2" />
    <our-data-container>
      <our-data-field label="Legal name">{{ provider.name }}</our-data-field>
      <our-data-field label="Trading names">{{ provider.tradingNames }}</our-data-field>
      <our-data-field label="SAP customer identifier(s)" empty="None">{{ sapCustomerIdentifiers }}</our-data-field>
      <our-data-field label="ABN">{{ provider.abn }}</our-data-field>
      <our-data-field label="ACN">{{ provider.acn }}</our-data-field>
      <our-data-field label="Provider code">{{ provider.providerCode }}</our-data-field>
      <our-data-field label="RTO code" empty="None">{{ provider.rtoCode }}</our-data-field>
      <our-data-field label="TEQSA code" empty="None">{{ provider.teqsaCode }}</our-data-field>
      <our-data-field label="CRICOS code" empty="None">{{ provider.cricosCode }}</our-data-field>
    </our-data-container>
    <v-divider class="mb-2" />
    <our-data-container>
      <our-data-field label="HE application status">{{ provider.heApplicationStatus }}</our-data-field>
      <our-data-field label="VET application status">{{ provider.vetApplicationStatus }}</our-data-field>
      <our-data-field label="FRLI approval date (HE)">{{ provider.frliApprovalDate | compactDate }}</our-data-field>
      <our-data-field label="Revocation reference date (HE)">{{ provider.revocationReferenceDate | compactDate }}</our-data-field>
      <our-data-field label="Organisation ownership">{{ provider.organisationOwnership }}</our-data-field>
      <our-data-field label="Organisation classification">{{ provider.organisationClassification }}</our-data-field>
      <our-data-field label="Approved loan types">
        <our-lookups-list :values="provider.approvedLoanTypes" :lookups="lookups.loanTypes"/>
      </our-data-field>
    </our-data-container>
  </div>
</template>

<script>
import * as action from "@/store/providers/action-types";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import { LIST_FOR_PROVIDER } from "@/store/levy-collection-providers/action-types";
import { compactDate } from "@/helpers/formatting.helper";
import _ from "underscore";

export default {
  mixins: [filterMixins, lookupMixins],
  props: {
    providerId: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      showSystemLogSearchDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.providers.view;
    },
    provider() {
      return this.state.data;
    },
    levyCollectionProviders() {
      return this.$store.state.levyCollectionProviders.listingForProvider.data;
    },
    loading() {
      return this.$store.state.levyCollectionProviders.listingForProvider.loading || this.state.loading;
    },
    sapCustomerIdentifiers() {
      const descriptions = this.provider.sapCustomerAccounts.map(a => `${a.sapCustomerIdentifier} (${a.companyCode})`);
      return descriptions.join(", ");
    },
    allVslApprovalPeriods() {
      if (!this.provider.vslApprovalPeriods) return "";
      return this.provider.vslApprovalPeriods
        .map(a => `${compactDate(a.vslStartDate)} to ${compactDate(a.vslEndDate)}`)
        .join(", ");
    },
    sector() {
      const he = this.provider.heApplicationStatus == "Approved";
      const vet = this.provider.vetApplicationStatus == "Approved";
      if (he && vet) {
        return "Dual sector";
      } else if (he) {
        return "HE";
      } else if (vet) {
        return "VSL";
      } else {
        return null;
      }
    },
    approvedLoanTypes() {
      const types = this.provider.approvedLoanTypes;
      if (types.length) {
        const descriptions = _(this.lookups.loanTypes).filter(desc => types.includes(desc.value));
        return _(descriptions)
          .pluck("text")
          .join(", ");
      } else {
        return "";
      }
    },
    selectedTab() {
      return this.$store.state.providers.tab.selected;
    }
  },
  watch: {
    providerId() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      if (!this.state.loaded || this.state.providerId != this.providerId) {
        this.$store.dispatch(action.VIEW, this.providerId);
      }
      const listingForProvider = this.$store.state.levyCollectionProviders.listingForProvider;
      if (!listingForProvider.loaded || listingForProvider.providerId != this.providerId) {
        this.$store.dispatch(LIST_FOR_PROVIDER, this.providerId);
      }
    }
  }
};
</script>
